// Pure priority + SLA logic (section 18). No Firebase.
import { SLA_HOURS, categoryForIssue, CATEGORIES } from '../constants';

const URGENT_ISSUES = new Set([
    'Product Reaction', 'Burning Sensation',
    'Delivered Status but Product Not Received',
    'Refund Pending', 'Refund Issued', 'Wrong Amount Paid',
    // Ekart — severe service failures
    'Ekart Lost Shipment', 'Ekart Fake Delivery', 'Ekart Delivered But Not Received',
    'Ekart Damaged Shipment', 'Ekart Wrong Delivery',
]);

const HIGH_ISSUES = new Set([
    'Delivery Delay', 'COD RTO Issue', 'Reship Required',
    'Replacement Required', 'Return Pickup Required', 'Long Pending Delivery',
    // Ekart — delivery-impacting
    'Ekart Delivery Delay', 'Ekart NDR', 'Ekart Returned To Origin',
    'Ekart Out For Delivery Too Long', 'Ekart Shipment Not Picked',
    'Ekart Customer Refused', 'Ekart Hub Delay',
]);

const MEDIUM_ISSUES = new Set(['No Result', 'Dandruff', 'Pimple', 'Dryness']);

/**
 * Infer ticket priority from issue type + context.
 * @param {string} issueType
 * @param {{ isRepeatCustomer?: boolean }} ctx
 */
export const inferPriority = (issueType, ctx = {}) => {
    if (ctx.isRepeatCustomer) return 'urgent';        // repeat complaint customer
    if (URGENT_ISSUES.has(issueType)) return 'urgent';
    if (HIGH_ISSUES.has(issueType)) return 'high';
    if (MEDIUM_ISSUES.has(issueType)) return 'medium';
    if (categoryForIssue(issueType) === CATEGORIES.GENERAL) return 'low';
    return 'medium';
};

// Default department for an issue type.
export const departmentForIssue = (issueType) => {
    const cat = categoryForIssue(issueType);
    if (cat === CATEGORIES.PRODUCT) return 'doctor';
    if (cat === CATEGORIES.DELIVERY) return 'courier';
    if (cat === CATEGORIES.REFUND) return 'accounts';
    return 'support';
};

// Company SLA policy: EVERY complaint must close within 72h (3 days).
// Doctor cases and refund cases are stricter — 24h maximum.
export const SLA_MAX_HOURS = 72;
export const SLA_STRICT_HOURS = 24;
export const slaHoursFor = (priority, issueType = '') => {
    const base = SLA_HOURS[priority] ?? SLA_HOURS.low;
    const cat = categoryForIssue(issueType);
    const cap = (cat === CATEGORIES.PRODUCT || cat === CATEGORIES.REFUND) ? SLA_STRICT_HOURS : SLA_MAX_HOURS;
    return Math.min(base, cap);
};

// Due timestamp from createdAt + SLA window for the priority (+ category cap).
export const computeDueAt = (priority, createdAt = Date.now(), issueType = '') =>
    createdAt + slaHoursFor(priority, issueType) * 60 * 60 * 1000;

// Live time-left descriptor for a ticket. Negative = overdue.
export const slaTimeLeft = (ticket, now = Date.now()) => {
    if (!ticket?.dueAt) return null;
    if (['closed', 'resolved'].includes(ticket.status)) return null;
    return ticket.dueAt - now;
};

// Is the ticket past its SLA and not yet closed/resolved?
export const isSlaBreached = (ticket, now = Date.now()) => {
    if (!ticket || !ticket.dueAt) return false;
    if (ticket.status === 'closed' || ticket.status === 'resolved') return false;
    return now > ticket.dueAt;
};

// How long past SLA (ms). 0 if not breached.
export const breachDurationMs = (ticket, now = Date.now()) =>
    isSlaBreached(ticket, now) ? now - ticket.dueAt : 0;

// All breached tickets, worst (longest overdue) first.
export const getBreachedTickets = (tickets = [], now = Date.now()) =>
    tickets.filter(t => isSlaBreached(t, now)).sort((a, b) => a.dueAt - b.dueAt);

// Breached tickets that still need a manager notification (idempotency guard).
export const breachedNeedingNotification = (tickets = [], now = Date.now()) =>
    getBreachedTickets(tickets, now).filter(t => !t.slaNotificationSent);
